import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { api_paths } from "../../config/api.paths";
import API from "../../config/axios";
import { SET_LOADING } from "../../redux/user";
import { toastErrorMessage, toastSuccessMessage } from "../../utils/utils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PaymentStatusModal from "../common/PaymentStatusModal";

function ReserveTicket({ event, onTicketsUpdated, purchasedHolderIds = new Set() }) {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [members, setMembers] = useState([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState(new Set());
  const [includeSelf, setIncludeSelf] = useState(true);
  const [purchasedTicketHolders, setPurchasedTicketHolders] = useState(new Set());
  const {
    // price,
    start_date,
  } = event; // empty => section hidden
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'failed', or 'cancelled'
  const { user } = useSelector((state) => state.USER_DATA);

  // ... fetchMembers and useEffect ...

  const handleProcessToPay = async () => {
    try {
      dispatch(SET_LOADING(true));

      const familyMembersArray = Array.from(selectedMemberIds);

      const isPrimaryEligible = isActiveMember(user || {});
      const primaryUserHasTicket = purchasedTicketHolders.has(user?._id);
      const effectiveIncludeSelf = includeSelf && !primaryUserHasTicket && isPrimaryEligible;

      const payload = {
        amount: Number(totalAmount),
        event_id: event._id,
        primary_included: effectiveIncludeSelf,
        member_id: user?._id,
        family_members: familyMembersArray,
        guests: [], // No guests for logged-in users
        // Name/Email/Phone not needed if member_id is passed, backend fetches them.
      };

      const response = await API.post(
        `${api_paths.member.payment.EVENT_CHECKOUT}?type=event&screen=event-detail`,
        payload
      );

      if (response.data?.payload?.token) {
        const token = response.data.payload.token;
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `${process.env.REACT_APP_PAYMENT_GATEWAY_URL}hosted-payments`;

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "ssl_txn_auth_token";
        input.value = token;

        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
      } else if (response.data?.payload?.success) {
        setPaymentStatus({ status: "success", payment: true });
        toastSuccessMessage(
          "RSVP Successful! Your attendance has been confirmed. Event tickets have been sent to your email and are also available in your Dashboard." ||
            response.data?.payload?.message ||
            "Tickets reserved successfully!"
        );
        // tell parent to refresh the single PURCHASED_TICKETS API
        // This will trigger a prop update (purchasedHolderIds) which triggers our sync effect
        if (typeof onTicketsUpdated === "function") await onTicketsUpdated();
      }
    } catch (err) {
      console.error("Event Reserve Error:", err);
      toastErrorMessage(err?.response?.data?.message || "Reservation failed");
      dispatch(SET_LOADING(false));
    } finally {
      // We don't call SET_LOADING(false) here if success, because onTicketsUpdated -> purchasedHolderIds update -> sync effect will handle it.
      // But if there's an error or no redirect, we should.
      // Safety check: if no prop change is expected, clear loading.
    }
  };

  // ... render ...

  const isActiveMember = (m) =>
    m?.is_membership_active &&
    m?.membership_expiry_date &&
    new Date(m.membership_expiry_date).getTime() >= new Date(start_date).getTime();

  useEffect(() => {
    const syncAndFetch = async () => {
      try {
        dispatch(SET_LOADING(true));

        // 1. Sync purchased holders first
        const nextHolders = new Set(purchasedHolderIds || []);
        setPurchasedTicketHolders(nextHolders);

        // 2. Fetch members
        const response = await API.get(api_paths.member.family_member.GET_MEMBER);
        if (response.status === 200) {
          const result = response?.data?.payload?.result || [];
          setMembers(result);

          // 3. Default selection: select all eligible family members who haven't purchased yet
          const initialSelected = new Set();
          result.forEach((m) => {
            if (!nextHolders.has(m._id) && isActiveMember(m)) {
              initialSelected.add(m._id);
            }
          });
          setSelectedMemberIds(initialSelected);
        }
      } catch (error) {
        console.error("Error syncing ReserveTicket:", error);
      } finally {
        dispatch(SET_LOADING(false));
      }
    };

    syncAndFetch();
    // eslint-disable-next-line
  }, [event._id, purchasedHolderIds]);

  // Toggle member selection
  const toggleMember = (member) => {
    const next = new Set(selectedMemberIds);
    if (next.has(member._id)) {
      next.delete(member._id);
    } else {
      next.add(member._id);
    }
    setSelectedMemberIds(next);
  };

  // Selections & Amount Calculation
  const availableMembers = members.filter(
    (m) => !purchasedTicketHolders.has(m._id) && isActiveMember(m)
  );
  const ineligibleMembersCount = members.filter((m) => !isActiveMember(m)).length;

  const isPrimaryEligible = isActiveMember(user || {});
  const primaryUserHasTicket = purchasedTicketHolders.has(user?._id);
  const effectiveIncludeSelf = includeSelf && !primaryUserHasTicket && isPrimaryEligible;

  const totalIneligibleCount = ineligibleMembersCount + (!isPrimaryEligible ? 1 : 0);
  const memberPrice = Number(user?.member_price) || 0;

  const totalTicketCount = selectedMemberIds.size + (effectiveIncludeSelf ? 1 : 0);
  const totalAmount = (totalTicketCount * memberPrice).toFixed(2);

  // Handle closing the modal and cleaning the URL
  const handleCloseModal = () => {
    setPaymentStatus(null);
    navigate(`/events/${id}`, { replace: true });
  };

  const handleRedirect = () => {
    setPaymentStatus(null);
    // Remove query params from URL without reloading
    navigate("/event-ticket", { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    const convergeId = params.get("converge_transaction_id");

    if (status) {
      setPaymentStatus({ status, convergeId });
    }
  }, [location]);

  return (
    <>
      <div className="ticket-card">
        <div className="ticket-card-header mb-4">
          <div className="px-4 pt-4 pb-2">
            <h1 className="ticket-card-title fw-semibold text-main f-20">Purchase Ticket</h1>
            <p className="ticket-card-subtitle mb-0 fs-14">
              Select family members to generate tickets
            </p>
          </div>
        </div>
        <div className="px-4 pb-3">
          {totalIneligibleCount > 0 && (
            <div className="alert alert-warning mb-2">
              <strong>
                Few
                {/* {totalIneligibleCount} */}
              </strong>{" "}
              member(s) are ineligible for this event due to inactive or expired membership.{" "}
              <button
                type="button"
                onClick={() => {
                  // Use history + popstate so SPA router (BrowserRouter) handles navigation without full reload
                  navigate("/membership");
                }}
                style={{
                  textDecoration: "underline",
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: "inherit",
                  fontWeight: "600",
                }}
                aria-label="Go to membership page"
              >
                Click here to purchase membership
              </button>
              .
            </div>
          )}

          {/* Logged-in user selector */}
          {user && !primaryUserHasTicket && isPrimaryEligible && (
            <div style={{ marginTop: 10, marginBottom: 8 }}>
              {/* <p style={{ fontWeight: 600, marginBottom: 6 }}>You</p> */}
              <button
                type="button"
                onClick={() => setIncludeSelf((prev) => !prev)}
                style={{
                  width: "100%",
                  justifyContent: "space-between",
                  borderRadius: 8,
                  padding: "10px 12px",
                  border: includeSelf ? "1px solid #F4C04A" : "1px solid #E6E6E6",

                  background: includeSelf ? "rgba(237,169,43,0.12)" : "#fff",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "none",
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 600 }}>
                  {user.first_name || user.last_name
                    ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                    : user.name || "You"}{" "}
                  (Primary)
                </span>
              </button>
            </div>
          )}

          {/* Members list */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {availableMembers.length === 0 ? (
              <div className="ticket-sub-desc fs-14 mt-3">
                No family members are there for RSVP. You can add them from your Dashboard.
              </div>
            ) : (
              availableMembers.map((m) => {
                const fullName = `${m.first_name || ""} ${m.last_name || ""}`.trim() || "Unknown";
                const isSelected = selectedMemberIds.has(m._id);

                const borderColor = isSelected ? "#F4C04A" : "#E6E6E6";
                const backgroundColor = isSelected ? "rgba(237,169,43,0.12)" : "#fff";

                return (
                  <button
                    key={m._id}
                    type="button"
                    onClick={() => toggleMember(m)}
                    style={{
                      width: "100%",
                      justifyContent: "space-between",
                      borderRadius: 8,
                      padding: "8px 12px",
                      border: `1px solid ${borderColor}`,
                      background: backgroundColor,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 8,
                      boxShadow: "none",
                    }}
                  >
                    <span style={{ fontSize: 14, fontWeight: 600 }}>
                      {fullName} ({m.relationship})
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {/* Amount summary */}
          <div className="mb-2">
            {/* <div className="amount-row mb-3">
            <span>
              Member Price: {activeFamilyMembers.length + (selfIsActive ? 1 : 0)} × $
              {memberPrice || 0}
            </span>
            <strong>${activeFamilyAmount + (selfIsActive ? memberPrice : 0)}</strong>
          </div> */}

            <div className="border-hr mt-4">
              {/* <div className="amount-row">Total Tickets: {totalTicketCount}</div> */}
              <div className="amount-row mt-2 pt-2">
                <span>Total Tickets</span>
                <strong>{totalTicketCount}</strong>
              </div>
              <div className="total-amount fw-semibold d-flex justify-content-between">
                <p className="fw-semibold">Total Amount</p>
                <span>${totalAmount}</span>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-orange-md w-100 mb-2"
              onClick={handleProcessToPay}
              disabled={totalTicketCount === 0}
            >
              {/* {totalAmount > 0 ? "Pay & Generate Ticket" : "Generate Ticket"} */}
              RSVP & Purchase Ticket
            </button>
          </div>
        </div>
      </div>

      {paymentStatus && (
        <PaymentStatusModal
          status={paymentStatus?.status}
          convergeId={paymentStatus?.convergeId}
          onClose={handleCloseModal}
          onNavigate={handleRedirect}
          buttonName={"event"}
          type={"event"}
          screen={"reserve"}
          payment={paymentStatus.payment}
        />
      )}
    </>
  );
}

export default ReserveTicket;
